import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as opaque from '@serenity-kit/opaque';
import { v4 as uuidv4 } from 'uuid';
import type { ActivePromoCode, EncryptedVaultItem, EntitlementSnapshot, ListOrgsResponse, Organization, OrgCoverage, OrgRole, PendingInvite, VaultItem } from '@heybesafe/shared';
import { decryptFamilyHandoff, encryptSharedItem, rotatePasswordHistory, mergeListItem, mergeListEntries } from '@heybesafe/shared';
import {
  decryptItem,
  deriveLocalWrappingKey,
  deriveWrappingKeyFromExportKey,
  encryptItem,
  generateLocalKdfSalt,
  generateVaultKey,
  unwrapVaultKey,
  wrapVaultKey,
} from '../crypto/crypto';
import {
  deriveAccountWrappingKey,
  deriveAccountPublicKey,
  generateAccountKeyPair,
  localUnwrapAccountPrivateKey,
  localWrapAccountPrivateKey,
  unwrapAccountPrivateKey,
  wrapAccountPrivateKey,
} from '../crypto/familyKeys';
import {
  deriveIdentityWrappingKey,
  generateIdentityKeyPair,
  localUnwrapIdentityPrivateKey,
  localWrapIdentityPrivateKey,
  unwrapIdentityPrivateKey,
  wrapIdentityPrivateKey,
  signKey,
} from '../crypto/identityKeys';
import type { SignedKey, SignedKeyFields } from '../crypto/identityKeys';
import { familyKeyAuthentic } from '../pkastore/familyKeyAuth';
import { base64UrlToBytes, bytesToBase64Url } from '../crypto/base64url';
import {
  cacheKey,
  clearAllLocalData,
  clearBiometricEnrollment,
  getBiometricEnrollment,
  getLocalWrappedAccountKey,
  getLocalWrappedIdentityKey,
  getMeta,
  removeMeta,
  setBiometricEnrollment,
  setLocalWrappedAccountKey,
  setLocalWrappedIdentityKey,
  setMeta,
} from '../storage/safe-store';
import {
  BiometricUnlockError,
  enrollBiometric,
  unwrapPasswordWithBiometric,
} from '../crypto/biometricUnlock';
import { startOnboarding } from '../onboarding/storage';
import {
  clearTokens,
  getCurrentUserId,
  getAccountKey,
  getIdentityKey,
  putIdentityKey,
  getOrg,
  getPendingInvites,
  listOrgInvites,
  listOrgs,
  logout as apiLogout,
  opaqueLoginFinish,
  opaqueLoginStart,
  opaqueRegisterFinish,
  opaqueRegisterStart,
  passwordRotateStart,
  passwordRotateFinish,
  pullVault,
  pushVault,
  putAccountKey,
  setEntitlementErrorHandler,
  storeTokens,
} from '../api/client';
import { fetchEntitlement } from '../api/entitlement';
import { useAccountKey } from './AccountKeyContext';
import { useFamilyKeys } from './FamilyKeysContext';
import { decryptSharedItem } from '../components/prepareShareForFamily';

// ─── Types ────────────────────────────────────────────────────────────────────

export type VaultStatus = 'loading' | 'unauthenticated' | 'locked' | 'unlocked';

/** One pending action: an externally-invited user signed up and the admin
 *  needs to perform the family-key handoff. */
export interface PendingFinalization {
  orgId: string;
  orgName: string;
  invitedEmail: string;
  token: string;
  createdAt: string;
}

/**
 * An org as carried in SafeContext: the shared `Organization` plus the caller's
 * role and cached per-family coverage (`covered`/`coverageReason`, from
 * `GET /orgs`). Coverage is absent on pre-backfill records → treated as covered
 * (fail-open). See 04-web-app / 02-server.
 */
type SafeOrg = Organization & {
  role: OrgRole;
  covered?: boolean;
  coverageReason?: OrgCoverage['reason'];
};

interface SafeContextValue {
  status: VaultStatus;
  items: VaultItem[];
  orgs: Array<SafeOrg>;
  pendingInvites: PendingInvite[];
  /**
   * Email-invites the signed-in user (as a family admin) needs to finalize
   * because the invitee just signed up. Drives the global PendingFinalizations
   * banner ("X has created an account — go finalize"), the red dot on the
   * Families nav entry, the red dot on the matching family card in the org
   * list, the red dot on that family's Manage tab, and the section-header
   * dot inside ManageTab itself. Aggregated across every org where the user
   * is admin; populated alongside `refreshOrgs`.
   */
  pendingFinalizations: PendingFinalization[];
  /**
   * False from `lockVault()` until the matching refetch resolves. `items` and
   * `orgs` are emptied on lock and `unlock()` flips status to 'unlocked' before
   * either fetch lands, so an empty array is ambiguous — it means "not loaded
   * yet" just as often as "you have none". Consult these before rendering an
   * absence ("not found", "your safe is empty"); see `LoadingPane`.
   *
   * Tracked separately because they resolve at different times: `orgs` after
   * one round-trip in `unlock()`, `items` only after the later `syncPull()`.
   */
  orgsLoaded: boolean;
  itemsLoaded: boolean;
  syncError: boolean;
  loginEmail: string | null;
  loadingStep: string | null;
  login:    (email: string, masterPassword: string) => Promise<{ ok: boolean; error?: string }>;
  register: (email: string, masterPassword: string, signupSource?: string) => Promise<{ ok: boolean; error?: string }>;
  unlock:   (masterPassword: string)                => Promise<{ ok: boolean; error?: string }>;
  verifyPassword: (password: string) => Promise<boolean>;
  /** Biometric unlock (WebAuthn PRF). `enabled`/`disable` manage the enrollment;
   *  `unlockWithBiometric` is the lock-screen path. `biometricEnrolled` reflects
   *  whether a credential is bound on this device. See
   *  web/src/crypto/biometricUnlock.ts. */
  biometricEnrolled: boolean;
  enableBiometricUnlock:  (safePassword: string) => Promise<{ ok: boolean; code?: string }>;
  disableBiometricUnlock: () => void;
  unlockWithBiometric:    () => Promise<{ ok: boolean; error?: string; code?: string }>;
  changeSafePassword: (currentPassword: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  lock:     () => void;
  logout:   () => Promise<void>;
  addItem:    (item: Omit<VaultItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<VaultItem>;
  updateItem: (id: string, updates: Partial<VaultItem>) => Promise<VaultItem>;
  deleteItem: (id: string) => Promise<void>;
  /** Re-stamp the circle scope of every item in a category (denormalization).
   *  `circleId` string sets the scope; `undefined` clears it to whole-family. */
  restampCategory: (orgId: string, categoryId: string, circleId: string | undefined) => void;
  /** Family private key for an org, if hydrated (see familyKeys.getKey).
   *  Used by attachment decrypt-on-view (Binder image attachments). */
  getFamilyPrivateKey: (orgId: string) => Uint8Array | undefined;
  sync:        () => Promise<void>;
  /** Force a full safe re-pull (drops `lastSyncedAt`). Used after joining a
   *  family: the family's shared items predate this client's `lastSyncedAt`,
   *  so an incremental sync would miss them — only a full pull picks them up. */
  resyncSafe:  () => Promise<void>;
  refreshOrgs: () => Promise<void>;
  refreshInvites: () => Promise<void>;
  refreshPendingFinalizations: () => Promise<void>;
  accountWrappingKey: CryptoKey | null;
  localWrappingKey: CryptoKey | null;
  vaultKey: CryptoKey | null;
  /** PKA: the current user's own Ed25519 identity public key (for safety numbers). */
  identityPublicKey: Uint8Array | null;
  /** PKA: sign a key bundle with the current user's identity private key; null
   *  if the identity key isn't loaded (e.g. locked or pre-PKA account). */
  signWithIdentityKey: (fields: SignedKeyFields) => SignedKey | null;
  // SP5: entitlement snapshot + transient banner
  entitlement: EntitlementSnapshot | null;
  transientBannerMessage: string | null;
  dismissTransientBanner: () => void;
  refreshEntitlement: () => Promise<void>;
  // SP8: email verification state from /subscriptions/me; null = loading or absent (legacy server)
  emailVerified: boolean | null;
  // SP9 (trial coupon codes): active in-app promo for trial users; null when none or non-trial
  activePromoCode: ActivePromoCode | null;
}

export const SafeContext = createContext<SafeContextValue | null>(null);

export function useSafe(): SafeContextValue {
  const ctx = useContext(SafeContext);
  if (!ctx) throw new Error('useSafe must be used within SafeProvider');
  return ctx;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AUTO_LOCK_MS = 5 * 60 * 1000;
// Grace period after the tab is hidden (switched away / minimized) before we
// lock. Leaving the tab for a moment — copying a code into another app, checking
// an email for a 2FA link — no longer locks instantly; returning within the
// window cancels the pending lock.
const AWAY_LOCK_MS = 60 * 1000;
const ENTITLEMENT_STORAGE_KEY = 'hbs:entitlement-snapshot';

// ─── Provider ─────────────────────────────────────────────────────────────────

export function SafeProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus]           = useState<VaultStatus>('loading');
  const [items, setItems]             = useState<VaultItem[]>([]);
  const [orgs, setOrgs]               = useState<Array<SafeOrg>>([]);
  const [orgsLoaded, setOrgsLoaded]   = useState(false);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [pendingFinalizations, setPendingFinalizations] = useState<PendingFinalization[]>([]);
  const [syncError, setSyncError]     = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [loginEmail, setLoginEmail] = useState<string | null>(() => getMeta('email'));
  const [biometricEnrolled, setBiometricEnrolled] = useState<boolean>(() => getBiometricEnrollment() !== null);

  const familyKeys = useFamilyKeys();
  const accountKeys = useAccountKey();

  // ── SP5: Entitlement snapshot + transient banner ──────────────────────────────

  const [entitlement, setEntitlement] = useState<EntitlementSnapshot | null>(() => {
    // Synchronous hydrate from sessionStorage so first render isn't blank.
    try {
      const raw = sessionStorage.getItem(ENTITLEMENT_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as EntitlementSnapshot) : null;
    } catch {
      return null;
    }
  });
  const [transientBannerMessage, setTransientBannerMessage] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [activePromoCode, setActivePromoCode] = useState<ActivePromoCode | null>(null);

  const refreshEntitlement = useCallback(async () => {
    const result = await fetchEntitlement();
    if (result.ok) {
      setEntitlement(result.data.entitlement);
      setEmailVerified(result.data.emailVerified ?? null);
      setActivePromoCode(result.data.activePromoCode ?? null);
      try {
        sessionStorage.setItem(ENTITLEMENT_STORAGE_KEY, JSON.stringify(result.data.entitlement));
      } catch { /* sessionStorage full or unavailable — silently degrade */ }
    }
  }, []);

  const dismissTransientBanner = useCallback(() => {
    setTransientBannerMessage(null);
  }, []);

  // Register the entitlement-error callback once on mount.
  //
  // Suppressed codes are gate-time denials handled inline at the call site:
  //   - NO_AVAILABLE_SUBSCRIPTION is effectively dead-code after the Phase 1
  //     gate fix, but the suppression keeps any out-of-date client from
  //     showing a misleading banner during the rollout window.
  //   - NO_ENTITLEMENT_CREATE_FAMILY surfaces inline on OrgListPage /
  //     OrgCreatePage (the Add-seats fork), so the banner would just be
  //     duplicate noise.
  //   - POOL_FULL (Phase 2) renders inline on ManageTab as an Add-seats
  //     CTA next to the invite form; the generic banner would be
  //     redundant.
  useEffect(() => {
    setEntitlementErrorHandler((body) => {
      void refreshEntitlement();
      if (
        body.code !== 'NO_AVAILABLE_SUBSCRIPTION' &&
        body.code !== 'NO_ENTITLEMENT_CREATE_FAMILY' &&
        body.code !== 'POOL_FULL' &&
        body.code !== 'TRIAL_FAMILY_LIMIT'
      ) {
        setTransientBannerMessage('Your subscription state changed. Please try again.');
      }
    });
    return () => setEntitlementErrorHandler(null);
  }, [refreshEntitlement]);

  // Refs — don't need to trigger re-renders
  const encKey           = useRef<CryptoKey | null>(null);
  const accountWrappingKey = useRef<CryptoKey | null>(null);
  // PKA: HKDF(exportKey, "identity-signing-key-wrapping") and the current user's
  // Ed25519 identity keypair (raw bytes), held for re-wrapping on rotation and
  // signing the account public key. Only the private key must stay secret.
  const identityWrappingKey = useRef<CryptoKey | null>(null);
  const identityPrivRef  = useRef<Uint8Array | null>(null);
  const identityPubRef   = useRef<Uint8Array | null>(null);
  // Bumped whenever the identity keypair changes, purely to force a re-render.
  // `identityPublicKey` is exposed to consumers off `identityPubRef.current`,
  // and a ref write is invisible to React — so a write that lands after the
  // last state update would otherwise leave every consumer holding `null` for
  // the rest of the session. That is exactly what broke family creation for
  // brand-new accounts once the key bootstrap moved off the critical path:
  // OrgCreatePage's `!identityPublicKey` gate reported "Finishing account
  // setup — try again in a moment." forever, and no retry could clear it.
  // AccountKeyContext carries the same bump for the same reason.
  const [, bumpIdentityKeyVersion] = useState(0);
  const localWrappingKey = useRef<CryptoKey | null>(null);
  const itemsRef         = useRef<VaultItem[]>([]);
  const orgsRef          = useRef<Array<SafeOrg>>([]);
  const deletedIdsRef    = useRef<string[]>([]);
  const dirtyIdsRef      = useRef<Set<string>>(new Set());
  // Indirection to schedulePush (defined below) so syncPull can trigger a
  // push-back after a list merge without a forward reference / TDZ.
  const schedulePushRef  = useRef<() => void>(() => {});
  // Indirection to syncPush, used to FLUSH a pending debounced push before the
  // tab is backgrounded/closed — otherwise an edit made within the 2 s debounce
  // window is lost (the timer is dropped or syncPush early-returns once the key
  // is cleared). The logout path flushes via syncPush directly (it's in scope).
  const syncPushRef      = useRef<() => Promise<void>>(async () => {});
  // Indirection to sync (pull+push), used by list edits and teardown flushes.
  const syncRef          = useRef<() => Promise<void>>(async () => {});
  // Debounce timer for list-edit syncs (a `sync()` = pull-merge-push, so a
  // concurrent member's entries are unioned in before the push instead of being
  // blindly overwritten by this client's copy). Separate from pushDebounceRef.
  const listSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ids whose circle scope was EXPLICITLY cleared (e.g. a category re-stamp
  // widening back to whole-family). syncPush sends `circleId: null` for these
  // so the server clears the stored scope; a normal edit of a whole-family
  // item just omits circleId (and the server preserves), which protects against
  // a stale client silently widening a scoped item.
  const explicitCircleClearRef = useRef<Set<string>>(new Set());
  const lockTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const awayTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pushDebounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the current user's circleIds per org (sorted joined string) so
  // refreshOrgs can detect a change and trigger a full re-pull.
  const myCircleIdsRef   = useRef<Map<string, string>>(new Map());

  // Keep itemsRef in sync with state
  useEffect(() => { itemsRef.current = items; }, [items]);
  // syncPull reads orgs to find each shared item's family pubkey; mirror into
  // a ref so the callback can stay deps-stable.
  useEffect(() => { orgsRef.current = orgs; }, [orgs]);

  /**
   * The ONLY way to write the identity keypair. Writes both refs and signals a
   * re-render, so consumers of `identityPublicKey` observe the change no matter
   * when it lands. Pass nulls to clear.
   */
  const setIdentityKeyPair = useCallback(
    (publicKey: Uint8Array | null, privateKey: Uint8Array | null) => {
      identityPubRef.current = publicKey;
      identityPrivRef.current = privateKey;
      bumpIdentityKeyVersion((v) => v + 1);
    },
    [bumpIdentityKeyVersion],
  );

  // ── Auto-lock ───────────────────────────────────────────────────────────────

  const lockVault = useCallback(() => {
    encKey.current = null;
    accountWrappingKey.current = null;
    identityWrappingKey.current = null;
    setIdentityKeyPair(null, null);
    localWrappingKey.current = null;
    familyKeys.clear();
    accountKeys.clear();
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    lockTimerRef.current = null;
    if (awayTimerRef.current) clearTimeout(awayTimerRef.current);
    awayTimerRef.current = null;
    setItems([]);
    setItemsLoaded(false);
    setOrgs([]);
    setOrgsLoaded(false);
    setPendingInvites([]);
    setLoginEmail(null);
    setEntitlement(null);
    setEmailVerified(null);
    setActivePromoCode(null);
    setTransientBannerMessage(null);
    try { sessionStorage.removeItem(ENTITLEMENT_STORAGE_KEY); } catch { /* ignore */ }
    setStatus('locked');
  }, [familyKeys, accountKeys, setIdentityKeyPair]);

  const resetLockTimer = useCallback(() => {
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    lockTimerRef.current = setTimeout(lockVault, AUTO_LOCK_MS);
  }, [lockVault]);

  useEffect(() => {
    const handleActivity = () => { if (encKey.current) resetLockTimer(); };
    // Leaving the tab starts a short grace countdown rather than locking
    // immediately; returning before it elapses cancels it.
    // Flush a pending debounced push immediately. The tab is still alive on
    // `hidden`, so the async push completes; without this, an edit made within
    // the 2 s debounce before the user switches away / closes the tab is lost
    // (e.g. checking a list item then switching to another family member).
    const flushPendingPush = () => {
      if (!encKey.current) return;
      const listPending = listSyncDebounceRef.current !== null;
      if (pushDebounceRef.current) { clearTimeout(pushDebounceRef.current); pushDebounceRef.current = null; }
      if (listSyncDebounceRef.current) { clearTimeout(listSyncDebounceRef.current); listSyncDebounceRef.current = null; }
      // A pending list edit flushes via a full sync (pull-merge-push) so it
      // converges with the server instead of blindly overwriting; other pending
      // edits just push. On `hidden` the tab is alive so the async work
      // completes; on `pagehide` it is best-effort.
      if (listPending) void syncRef.current();
      else void syncPushRef.current();
    };

    const handleVisibility = () => {
      if (!encKey.current) return;
      if (document.hidden) {
        flushPendingPush();
        if (awayTimerRef.current) clearTimeout(awayTimerRef.current);
        awayTimerRef.current = setTimeout(lockVault, AWAY_LOCK_MS);
      } else if (awayTimerRef.current) {
        clearTimeout(awayTimerRef.current);
        awayTimerRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('visibilitychange', handleVisibility);
    // pagehide fires on reload / close / bfcache; best-effort flush (an async
    // push may not finish on a hard close, but covers reload and bfcache).
    window.addEventListener('pagehide', flushPendingPush);
    return () => {
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', flushPendingPush);
      if (awayTimerRef.current) { clearTimeout(awayTimerRef.current); awayTimerRef.current = null; }
    };
  }, [lockVault, resetLockTimer]);

  // ── Initial load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const salt  = getMeta('localKdfSalt');
    const key   = getMeta('localWrappedSafeKey');
    const iv    = getMeta('localWrappedSafeKeyIv');
    const email = getMeta('email');
    const hasRefreshToken = !!localStorage.getItem('refreshToken');
    if (salt && key && iv && email && hasRefreshToken) {
      setStatus('locked');
    } else {
      setStatus('unauthenticated');
    }
  }, []);

  // The api/client dispatches hbs:auth-expired the first time /auth/refresh
  // returns non-ok. Force the app into 'unauthenticated' immediately so the
  // user lands on the login screen rather than the unlock screen (which would
  // be misleading — there's no valid session to unlock back into).
  useEffect(() => {
    const onAuthExpired = (): void => {
      encKey.current = null;
      setItems([]);
      setItemsLoaded(false);
      setOrgs([]);
      setOrgsLoaded(false);
      setPendingInvites([]);
      setEntitlement(null);
      setEmailVerified(null);
      setActivePromoCode(null);
      setTransientBannerMessage(null);
      try { sessionStorage.removeItem(ENTITLEMENT_STORAGE_KEY); } catch { /* ignore */ }
      setStatus('unauthenticated');
    };
    window.addEventListener('hbs:auth-expired', onAuthExpired);
    return () => { window.removeEventListener('hbs:auth-expired', onAuthExpired); };
  }, []);

  // ── Sync ─────────────────────────────────────────────────────────────────────

  const syncPull = useCallback(async (opts?: { suppressAuthRedirect?: boolean }): Promise<{ authExpired?: boolean }> => {
    const key = encKey.current;
    if (!key) return {};

    const lastSyncedAt = getMeta('lastSyncedAt') ?? undefined;
    const result = await pullVault(lastSyncedAt);
    if (!result.ok) {
      if (result.code === 'NOT_AUTHENTICATED' && encKey.current) {
        if (opts?.suppressAuthRedirect) {
          // Caller (unlock) wants to handle this itself.
          return { authExpired: true };
        }
        // Refresh token expired while vault was unlocked — force full re-login.
        await clearTokens();
        encKey.current = null;
        setItems([]);
        setItemsLoaded(false);
        setOrgs([]);
        setOrgsLoaded(false);
        setPendingInvites([]);
        setStatus('unauthenticated');
      } else if (result.code !== 'NOT_AUTHENTICATED') {
        setSyncError(true);
      }
      return {};
    }

    const { items: serverItems, deletedIds, serverTime } = result.data;

    const decrypted: VaultItem[] = [];
    for (const encItem of serverItems) {
      // Phase A: items with `ephemeralPublicKey` are ECIES-wrapped under the
      // family's static pubkey (see prepareShareForFamily). They CANNOT be
      // decrypted with the personal vault key — that's a silent AES auth-tag
      // failure and the item disappears from the UI. Route them to the ECIES
      // helper using the recipient's cached family privkey instead.
      if (encItem.ephemeralPublicKey && encItem.orgId) {
        const familyPrivKey = familyKeys.getKey(encItem.orgId);
        const org = orgsRef.current.find(o => o.id === encItem.orgId);
        if (!familyPrivKey || !org?.publicKey) {
          // Key (or org metadata) not yet hydrated — happens briefly right
          // after invite acceptance while orgs refresh is still in flight.
          // Skip; the next syncPull will retry once the key lands.
          console.warn(`Skipping shared item ${encItem.id}: family key not yet loaded for org ${encItem.orgId}`);
          continue;
        }
        try {
          const item = await decryptSharedItem(encItem, familyPrivKey, base64UrlToBytes(org.publicKey));
          // Thread plaintext server metadata not part of the ciphertext
          if (encItem.circleId) item.circleId = encItem.circleId;
          decrypted.push(item);
        } catch (e) {
          console.error(`Failed to ECIES-decrypt shared item ${encItem.id}`, e);
        }
        continue;
      }
      try {
        const item = await decryptItem<VaultItem>(encItem.ciphertext, encItem.iv, key);
        decrypted.push(item);
      } catch {
        console.error(`Failed to decrypt item ${encItem.id}`);
      }
    }

    // Reconcile `list` items against the local copy. Shared-item sync is blind
    // whole-item last-write-wins (the server can't merge an E2E-encrypted item),
    // so a collaboratively-edited list would lose a concurrent add/check if the
    // incoming server copy simply replaced the local one. For list items we
    // instead union entries (listMerge); if the merge contributed entries the
    // server didn't have, mark the item dirty so the superset is pushed back,
    // and convergence settles in one extra round-trip. See the Binder-lists spec.
    const localSnapshot = itemsRef.current;
    const pushBackIds: string[] = [];
    const reconciled = decrypted.map((inc) => {
      if (inc.type !== 'list') return inc;
      const k = cacheKey(inc.orgId, inc.id);
      const localItem = localSnapshot.find(i => cacheKey(i.orgId, i.id) === k);
      if (!localItem) return inc;
      const mergedItem = mergeListItem(localItem, inc);
      // Did the local copy add anything beyond the incoming (normalized) set?
      const normalizedIncoming = mergeListEntries(inc.listItems ?? []);
      if (JSON.stringify(mergedItem.listItems) !== JSON.stringify(normalizedIncoming)) {
        pushBackIds.push(inc.id);
      }
      return mergedItem;
    });

    // Phase A: dedup/merge by composite (orgId ?? 'self', id). The server
    // aggregates items across personal + org partitions, so the same id can
    // legally appear under different orgIds; keying by id alone would lose
    // one of them. `deletedIds` is still keyed by bare id (server contract
    // pre-Phase-A); since deletion responses for org items will arrive on
    // sync from the same partition that owns the item, we filter any matching
    // id regardless of partition.
    //
    // Computed from `itemsRef.current` (not a functional updater) so we can set
    // the ref SYNCHRONOUSLY below — a `sync()` (pull then push) must have the
    // merged union visible to `syncPush` immediately, before React flushes the
    // `items` effect. Without this, a list pull-merge-push would push the stale
    // pre-merge local copy and the union would never reach the server.
    const prevItems = itemsRef.current;
    const afterDeletions = prevItems.filter(i => !deletedIds.includes(i.id));
    const serverKeys = new Set(reconciled.map(d => cacheKey(d.orgId, d.id)));
    const mergedExisting = afterDeletions.map(i => {
      const k = cacheKey(i.orgId, i.id);
      return serverKeys.has(k) ? reconciled.find(d => cacheKey(d.orgId, d.id) === k)! : i;
    });
    const localKeys = new Set(afterDeletions.map(i => cacheKey(i.orgId, i.id)));
    const newItems = reconciled.filter(d => !localKeys.has(cacheKey(d.orgId, d.id)));
    const finalItems = [...mergedExisting, ...newItems];
    itemsRef.current = finalItems;
    setItems(finalItems);

    // Push the merged supersets back so other members converge on the union.
    if (pushBackIds.length > 0) {
      for (const id of pushBackIds) dirtyIdsRef.current.add(id);
      schedulePushRef.current();
    }

    // The pull succeeded and `items` now reflects the server — anything still
    // missing from it is genuinely absent, not merely un-fetched.
    setItemsLoaded(true);
    setMeta('lastSyncedAt', serverTime);
    setSyncError(false);
    return {};
  }, []);

  const syncPush = useCallback(async () => {
    const key = encKey.current;
    if (!key) return;

    const currentItems = itemsRef.current;
    const dirtyIds = dirtyIdsRef.current;
    const orgs = orgsRef.current;

    // Every dirty item is pushed: any family member may create or edit a
    // shared item, and the server answers with rejectedItemIds for anything
    // it refuses (e.g. an uncovered family).
    const writableItems = currentItems.filter((item) => dirtyIds.has(item.id));

    const deletedIds = [...deletedIdsRef.current];

    // Short-circuit: nothing to push → skip the network call entirely.
    if (writableItems.length === 0 && deletedIds.length === 0) {
      return;
    }

    const encrypted: EncryptedVaultItem[] = [];
    // Org items whose family public key isn't loaded yet are NOT pushed and
    // NOT personal-encrypted — they stay dirty and retry on a later sync.
    const deferredOrgItemIds = new Set<string>();
    for (const item of writableItems) {
      try {
        if (item.orgId) {
          const org = orgs.find((o) => o.id === item.orgId);
          if (!org?.publicKey) {
            console.warn(`Deferring shared item ${item.id}: family public key for org ${item.orgId} not loaded`);
            deferredOrgItemIds.add(item.id);
            continue;
          }
          // PKA (A2): never wrap an item to a family public key we can't
          // authenticate — a malicious server could have re-substituted
          // org.publicKey since hydrate. Defer (stay dirty) so a later sync,
          // once the founder signature verifies, pushes it. Enforcement is
          // universal — an unsigned family fails here too (no grandfathering).
          if (!familyKeyAuthentic(org)) {
            console.warn(`Deferring shared item ${item.id}: family key for org ${item.orgId} failed authentication`);
            deferredOrgItemIds.add(item.id);
            continue;
          }
          // circleId is plaintext envelope metadata — never bake it into the ciphertext.
          // Strip it from the plaintext payload, then re-attach it on the envelope:
          //   • a circleId string → set the scope;
          //   • no circleId but explicitly cleared → `null` (clear it server-side);
          //   • no circleId, not cleared → omit (server preserves the stored value).
          const { circleId, ...itemForEncrypt } = item;
          const clearing = explicitCircleClearRef.current.has(item.id);
          const encEnvelope = await encryptSharedItem(itemForEncrypt as VaultItem, base64UrlToBytes(org.publicKey));
          encrypted.push({
            ...encEnvelope,
            ...(item.type ? { type: item.type } : {}),
            ...(circleId ? { circleId } : clearing ? { circleId: null } : {}),
          });
        } else {
          const { ciphertext, iv } = await encryptItem(item, key);
          encrypted.push({ id: item.id, ciphertext, iv, clientUpdatedAt: item.updatedAt, orgId: item.orgId });
        }
      } catch {
        console.error(`Failed to encrypt item ${item.id}`);
      }
    }

    const result = await pushVault({ items: encrypted, deletedIds });

    if (result.ok) {
      // Build new ref values: rejected ids stay dirty / pending for the
      // next push to retry. Deferred org items (missing family key) also
      // stay dirty so they are retried on the next sync. Everything else clears.
      const newDirty = new Set<string>([...result.data.rejectedItemIds, ...deferredOrgItemIds]);
      const newDeletedIds = [...result.data.rejectedDeletedIds];
      dirtyIdsRef.current = newDirty;
      deletedIdsRef.current = newDeletedIds;
      // Keep the explicit-clear flag only for ids that stayed dirty (rejected /
      // deferred); a successfully-pushed clear is now committed server-side.
      explicitCircleClearRef.current = new Set(
        [...explicitCircleClearRef.current].filter((id) => newDirty.has(id)),
      );
      setMeta('lastSyncedAt', result.data.syncedAt);
      setSyncError(false);
      if (result.data.rejectedItemIds.length > 0 || result.data.rejectedDeletedIds.length > 0) {
        console.warn(
          '[sync] server rejected ids — will retry on next push',
          { items: result.data.rejectedItemIds, deletes: result.data.rejectedDeletedIds },
        );
        // A rejection is almost always an uncovered family (its writes are
        // gated server-side). Uncovered families are hidden, so this is rarely
        // hit — but surface it rather than dropping the write silently.
        setTransientBannerMessage('Some items couldn’t be saved — that family is no longer active.');
      }
    } else {
      if (result.code !== 'NOT_AUTHENTICATED') setSyncError(true);
    }
  }, []);

  const schedulePush = useCallback(() => {
    if (pushDebounceRef.current) clearTimeout(pushDebounceRef.current);
    pushDebounceRef.current = setTimeout(syncPush, 2000);
  }, [syncPush]);
  schedulePushRef.current = schedulePush;
  syncPushRef.current = syncPush;

  const sync = useCallback(async () => {
    await syncPull();
    await syncPush();
  }, [syncPull, syncPush]);
  syncRef.current = sync;

  // List edits reconcile with the server before pushing (read-modify-write):
  // the server does blind whole-item last-write-wins and cannot merge, so a
  // plain push of a collaboratively-edited list would clobber a concurrent
  // member's add/check. A `sync()` first pulls + merges (union into itemsRef,
  // synchronously — see syncPull) then pushes the union, so both members'
  // entries survive. Debounced short (interactive) and distinct from
  // schedulePush. See the Binder-lists spec (web convergence).
  const scheduleListSync = useCallback(() => {
    if (listSyncDebounceRef.current) clearTimeout(listSyncDebounceRef.current);
    listSyncDebounceRef.current = setTimeout(() => { void sync(); }, 800);
  }, [sync]);

  // Force a full re-pull: drop lastSyncedAt so syncPull fetches every item,
  // not just those changed since the last sync. Callers use this after a
  // membership change (e.g. accepting a family invite) surfaces items whose
  // server timestamps predate lastSyncedAt and would be missed incrementally.
  // Mirrors the circleChanged path in refreshOrgs and the unlock full-pull.
  const resyncSafe = useCallback(async () => {
    removeMeta('lastSyncedAt');
    await syncPull();
  }, [syncPull]);

  // v2: decrypt each org's familyKeyWrap with the account keypair and cache
  // the family private key. One path — used identically by login and unlock.
  const hydrateFamilyKeys = useCallback(async (
    orgs: ListOrgsResponse['orgs'],
  ) => {
    const accountPub = accountKeys.publicKey;
    const accountPriv = accountKeys.privateKey;
    if (!accountPub || !accountPriv) return;
    for (const org of orgs) {
      if (!org.familyKeyWrap) continue;
      if (familyKeys.getKey(org.id)) continue; // already cached
      // PKA: authenticate the family public key before installing its private
      // key. A malicious server can't forge the founder's signature, so a
      // fabricated/substituted key is rejected — and enforcement is universal,
      // so an unsigned family is rejected too (no grandfathering).
      if (!familyKeyAuthentic(org)) {
        console.error(`[pka] family key for org ${org.id} failed authentication — not installing`);
        continue;
      }
      try {
        const familyPriv = await decryptFamilyHandoff(
          org.familyKeyWrap, accountPriv, accountPub,
        );
        familyKeys.addKey(org.id, familyPriv);
      } catch (e) {
        console.error(`[familykey] failed to decrypt family key for org ${org.id}`, e);
      }
    }
    // No signature backfill: family creation always signs (see OrgCreatePage),
    // and enforcement is universal, so there are no unsigned families to repair.
  }, [accountKeys, familyKeys]);

  // Sum awaiting-finalization email-invites across every org where the user
  // is an admin. Drives the Families nav attention highlight: lights up when
  // an externally-invited user has signed up and the admin needs to finalize
  // the handoff. Per-org listOrgInvites failures are skipped so a single
  // 403/500 doesn't blank the whole count.
  //
  // The override arg exists because orgsRef is synced to `orgs` via useEffect,
  // which runs after render — so a refreshOrgs → refreshPendingFinalizations
  // chain in the same tick would see stale orgs. Callers that just fetched a
  // fresh list pass it in; standalone callers read the ref.
  //
  // Declared BEFORE refreshOrgs so it can sit in refreshOrgs's deps array
  // without a TDZ error.
  const refreshPendingFinalizations = useCallback(async (
    overrideOrgs?: Array<SafeOrg>,
  ) => {
    const source = overrideOrgs ?? orgsRef.current;
    const adminOrgs = source.filter((o) => o.role === 'admin');
    if (adminOrgs.length === 0) {
      setPendingFinalizations([]);
      return;
    }
    const results = await Promise.all(
      adminOrgs.map((o) => listOrgInvites(o.id)
        .then((r) => ({ org: o, result: r }))
        .catch(() => null),
      ),
    );
    const next: PendingFinalization[] = [];
    for (const r of results) {
      if (!r || !r.result.ok) continue;
      for (const inv of r.result.data.emailInvites) {
        if (inv.state !== 'awaiting-finalization') continue;
        next.push({
          orgId: r.org.id,
          orgName: r.org.name,
          invitedEmail: inv.invitedEmail,
          token: inv.token,
          createdAt: inv.createdAt,
        });
      }
    }
    next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    setPendingFinalizations(next);
  }, []);

  const refreshOrgs = useCallback(async () => {
    const result = await listOrgs();
    if (!result.ok) return;
    setOrgs(result.data.orgs);
    setOrgsLoaded(true);
    orgsRef.current = result.data.orgs;
    await hydrateFamilyKeys(result.data.orgs);
    await refreshPendingFinalizations(result.data.orgs);

    // Detect circle membership changes for the current user. If circleIds
    // changed since the last refresh, drop lastSyncedAt so the next syncPull
    // is a full re-pull and picks up newly-visible circle items.
    const myUserId = getCurrentUserId();
    if (myUserId && result.data.orgs.length > 0) {
      let circleChanged = false;
      await Promise.all(
        result.data.orgs.map(async (org) => {
          const orgResult = await getOrg(org.id);
          if (!orgResult.ok) return;
          const me = orgResult.data.members.find(
            (m: { userId: string; circleIds?: string[] }) => m.userId === myUserId,
          );
          const newKey = [...(me?.circleIds ?? [])].sort().join(',');
          const prev = myCircleIdsRef.current.get(org.id);
          if (prev !== undefined && prev !== newKey) {
            circleChanged = true;
          }
          myCircleIdsRef.current.set(org.id, newKey);
        }),
      );
      if (circleChanged) {
        removeMeta('lastSyncedAt');
        await syncPull();
      }
    }
  }, [hydrateFamilyKeys, refreshPendingFinalizations, syncPull]);

  const refreshInvites = useCallback(async () => {
    const result = await getPendingInvites();
    if (result.ok) setPendingInvites(result.data.invites);
  }, []);

  // ── Post-auth setup ───────────────────────────────────────────────────────────

  const afterAuth = useCallback(async (
    vaultKey: CryptoKey,
    email: string,
    masterPassword: string,
    // Set on the register path: the account keypair was generated and published
    // atomically with account creation. Install it here so this session has it
    // in memory + a local wrap, and the deferred bootstrap below skips the
    // account-key GET/generate (it only needs to handle the identity key).
    preAccountKey?: { publicKey: Uint8Array; privateKey: Uint8Array },
  ) => {
    const localSalt = generateLocalKdfSalt();
    const lwk = await deriveLocalWrappingKey(masterPassword, email, localSalt);
    localWrappingKey.current = lwk;
    const { wrapped: localWrappedVaultKey, iv: localWrappedVaultKeyIv } = await wrapVaultKey(vaultKey, lwk);

    if (preAccountKey) {
      // Uses the local `lwk` (not the ref) so a mid-flight auto-lock can't
      // null it out from under us.
      accountKeys.setKeyPair(preAccountKey.publicKey, preAccountKey.privateKey);
      const localWrap = await localWrapAccountPrivateKey(preAccountKey.privateKey, lwk);
      setLocalWrappedAccountKey(localWrap.ciphertext, localWrap.iv);
    }

    setMeta('email', email);
    setLoginEmail(email);
    setMeta('localKdfSalt', localSalt);
    setMeta('localWrappedSafeKey', localWrappedVaultKey);
    setMeta('localWrappedSafeKeyIv', localWrappedVaultKeyIv);

    encKey.current = vaultKey;
    resetLockTimer();
    setStatus('unlocked');

    // Load orgs for sidebar admin check + any pending invites in parallel.
    const [orgsResult, invitesResult] = await Promise.all([listOrgs(), getPendingInvites()]);
    if (orgsResult.ok) {
      setOrgs(orgsResult.data.orgs);
      setOrgsLoaded(true);
      void refreshPendingFinalizations(orgsResult.data.orgs);
      // syncPull (called later in this same async chain) reads orgsRef to find
      // each shared item's family pubkey. setOrgs only schedules the update;
      // mirror it into the ref synchronously so the ECIES path sees the orgs
      // before React flushes the next render.
      orgsRef.current = orgsResult.data.orgs;
    }
    if (invitesResult.ok) setPendingInvites(invitesResult.data.invites);

    // Phase 3: account-key bootstrap. Mirrors the Phase 2 family-key flow above
    // but for the user's single account-level keypair (used by the promote-to-
    // admin handoff). After OPAQUE login/register we have both wrapping keys:
    //   - accountWrappingKey: HKDF(exportKey, "account-key-wrapping") — server wrap
    //   - localWrappingKey:   PBKDF2(masterPassword, email) — local wrap
    // Path A: server already has a keypair → fetch, unwrap, cache, ensure local wrap.
    // Path B: server returns 404 → lazy-generate, server-wrap+PUT, local-wrap+cache.
    //
    // Wrapped in a helper so it can run BEFORE syncPull for family users (the
    // account key feeds hydrateFamilyKeys, which decrypts shared items) or be
    // DEFERRED after syncPull for org-less users — the account-creation critical
    // path, where nothing in syncPull needs these keys (see the call site below).
    const bootstrapAccountAndIdentityKeys = async () => {
    // Skip the account-key GET/generate when it was already published atomically
    // at registration (accountKeys already holds it). Login/unlock of existing
    // users still runs this block (publicKey is null until fetched).
    if (accountWrappingKey.current && localWrappingKey.current && !accountKeys.publicKey) {
      const existing = await getAccountKey();
      if (existing.ok) {
        try {
          const privBytes = await unwrapAccountPrivateKey(
            existing.data.wrappedAccountPrivateKey,
            existing.data.wrappedAccountPrivateKeyIv,
            accountWrappingKey.current,
          );
          // PKA finding H1: derive our OWN account public key from the
          // just-unwrapped private key — never trust the server's copy. The
          // private key is authentic (unwrapped under HKDF(exportKey), which the
          // server never sees), so a server that substitutes accountPublicKey
          // can no longer make us self-wrap the family key to a key it controls
          // (the self-wrap at family creation reads this cached public key). The
          // server's stored copy is consumed only by peers, who authenticate it
          // via its identity signature; if it disagrees with ours it's either
          // tampering (peers fail closed) or a corrupt record, so we warn rather
          // than trust it. The record is immutable server-side
          // (attribute_not_exists guard), so a client repair isn't possible —
          // and isn't needed, since nothing local uses the server's copy.
          const pubBytes = deriveAccountPublicKey(privBytes);
          if (existing.data.accountPublicKey !== bytesToBase64Url(pubBytes)) {
            console.warn('Server account public key disagrees with the locally-derived key — using the derived key');
          }
          accountKeys.setKeyPair(pubBytes, privBytes);

          // Refresh the local wrap on every login. afterAuth regenerates
          // localKdfSalt above, which rotates localWrappingKey — any wrap
          // from a prior session is encrypted under a stale key and would
          // fail to AES-GCM-decrypt on the next unlock().
          const local = await localWrapAccountPrivateKey(privBytes, localWrappingKey.current);
          setLocalWrappedAccountKey(local.ciphertext, local.iv);
        } catch (e) {
          // The server wrap couldn't be opened under the current account wrapping
          // key (derived from the OPAQUE export key) — the account key is
          // unrecoverable on this login. Flag it so the UI prompts the user
          // rather than silently blocking family features / decryption.
          console.error('Failed to unwrap existing account key', e);
          accountKeys.markRecoveryFailed();
        }
      } else {
        try {
          const { publicKey, privateKey } = await generateAccountKeyPair();
          const serverWrap = await wrapAccountPrivateKey(privateKey, accountWrappingKey.current);
          const result = await putAccountKey({
            accountPublicKey: bytesToBase64Url(publicKey),
            wrappedAccountPrivateKey: serverWrap.ciphertext,
            wrappedAccountPrivateKeyIv: serverWrap.iv,
          });
          if (result.ok) {
            const local = await localWrapAccountPrivateKey(privateKey, localWrappingKey.current);
            setLocalWrappedAccountKey(local.ciphertext, local.iv);
            accountKeys.setKeyPair(publicKey, privateKey);
          } else if (result.code === 'ALREADY_EXISTS') {
            // Race: another session uploaded first. Next sign-in's GET path
            // will succeed — log and move on.
            console.warn('Account key creation race: server already has one');
          } else {
            console.error('Failed to PUT account key', result.error);
          }
        } catch (e) {
          console.error('Failed to lazily generate account key', e);
        }
      }
    }

    // PKA: identity-key bootstrap + account-key self-signature. Same Path A/B
    // shape as the account key. The Ed25519 identity key signs the account public
    // key so peers (recovery contacts, family members) can authenticate it and a
    // malicious server can't substitute it (finding #5). Needs the account
    // keypair established above; skipped if that didn't land this login.
    if (
      identityWrappingKey.current && localWrappingKey.current
      && accountKeys.publicKey && accountKeys.privateKey
    ) {
      const userId = getCurrentUserId();
      try {
        const existing = await getIdentityKey();
        if (existing.ok) {
          const privBytes = await unwrapIdentityPrivateKey(
            existing.data.wrappedIdentityPrivateKey,
            existing.data.wrappedIdentityPrivateKeyIv,
            identityWrappingKey.current,
          );
          setIdentityKeyPair(base64UrlToBytes(existing.data.identityPublicKey), privBytes);
          const local = await localWrapIdentityPrivateKey(privBytes, localWrappingKey.current);
          setLocalWrappedIdentityKey(local.ciphertext, local.iv);
        } else {
          const { publicKey, privateKey } = generateIdentityKeyPair();
          const serverWrap = await wrapIdentityPrivateKey(privateKey, identityWrappingKey.current);
          // Self-sign the account public key with the fresh identity key (also
          // backfills the signature for users who already had an account key),
          // uploaded in the same write.
          const accountPublicKeySig = userId
            ? signKey(privateKey, {
              key: bytesToBase64Url(accountKeys.publicKey),
              keyType: 'account-x25519',
              subject: userId,
              context: '',
              notBefore: new Date().toISOString(),
              signerId: userId,
            })
            : undefined;
          const result = await putIdentityKey({
            identityPublicKey: bytesToBase64Url(publicKey),
            wrappedIdentityPrivateKey: serverWrap.ciphertext,
            wrappedIdentityPrivateKeyIv: serverWrap.iv,
            accountPublicKeySig,
          });
          if (result.ok) {
            const local = await localWrapIdentityPrivateKey(privateKey, localWrappingKey.current);
            setLocalWrappedIdentityKey(local.ciphertext, local.iv);
            setIdentityKeyPair(publicKey, privateKey);
          } else if (result.code === 'ALREADY_EXISTS') {
            console.warn('Identity key creation race: server already has one');
          } else {
            console.error('Failed to PUT identity key', result.error);
          }
        }
      } catch (e) {
        console.error('Failed to establish identity key', e);
      }
    }
    };

    const hasOrgs = orgsResult.ok && orgsResult.data.orgs.length > 0;
    if (hasOrgs) {
      // Family user: the account private key decrypts the family handoff in
      // hydrateFamilyKeys, and the family keys decrypt shared items in syncPull.
      // So the bootstrap must complete before both — keep it on the critical path.
      await bootstrapAccountAndIdentityKeys();
      await hydrateFamilyKeys(orgsResult.data.orgs);
      setLoadingStep('Loading safe…');
      await syncPull();
    } else {
      // No families (the account-creation path, and any org-less user): nothing
      // in syncPull needs the account/identity keys, so show the safe first and
      // establish those keys in the background — off the account-creation
      // critical path. A brand-new user who immediately opens "create family"
      // before this finishes hits OrgCreatePage's retryable "finishing account
      // setup" gate, so deferring is safe.
      setLoadingStep('Loading safe…');
      await syncPull();
      void bootstrapAccountAndIdentityKeys();
    }
    void refreshEntitlement();  // best-effort — UI degrades gracefully if it fails
  }, [resetLockTimer, syncPull, accountKeys, hydrateFamilyKeys, refreshEntitlement, setIdentityKeyPair]);

  // ── Auth actions ──────────────────────────────────────────────────────────────

  const register = useCallback(async (email: string, masterPassword: string, signupSource?: string) => {
    await opaque.ready;
    try {
      setLoadingStep('Contacting server…');
      const { clientRegistrationState, registrationRequest } = opaque.client.startRegistration({ password: masterPassword });

      const startResult = await opaqueRegisterStart({ email, registrationRequest });
      if (!startResult.ok) return { ok: false, error: startResult.error };

      const { registrationRecord, exportKey } = opaque.client.finishRegistration({
        clientRegistrationState,
        registrationResponse: startResult.data.registrationResponse,
        password: masterPassword,
      });

      setLoadingStep('Generating safe key…');
      const vaultKey = await generateVaultKey();
      const exportWrappingKey = await deriveWrappingKeyFromExportKey(exportKey);
      accountWrappingKey.current = await deriveAccountWrappingKey(exportKey);
      identityWrappingKey.current = await deriveIdentityWrappingKey(exportKey);
      const { wrapped: encryptedVaultKey, iv: encryptedVaultKeyIv } = await wrapVaultKey(vaultKey, exportWrappingKey);

      // Generate the account keypair NOW and publish it ATOMICALLY with the
      // account (below). Historically the account key was published by a
      // deferred, best-effort bootstrap AFTER signup "succeeded"; if that step
      // was interrupted (tab backgrounded mid-publish → auto-lock nulls the
      // wrapping key, app suspended, or a transient error), the user was left
      // silently un-invitable to families. Sending it with register/finish
      // removes that window entirely.
      const accountKeyPair = await generateAccountKeyPair();
      const accountServerWrap = await wrapAccountPrivateKey(accountKeyPair.privateKey, accountWrappingKey.current);

      setLoadingStep('Creating account…');
      const finishResult = await opaqueRegisterFinish({
        email, registrationRecord, encryptedVaultKey, encryptedVaultKeyIv, signupSource,
        accountPublicKey: bytesToBase64Url(accountKeyPair.publicKey),
        wrappedAccountPrivateKey: accountServerWrap.ciphertext,
        wrappedAccountPrivateKeyIv: accountServerWrap.iv,
      });
      if (!finishResult.ok) return { ok: false, error: finishResult.error };

      await storeTokens(finishResult.data);
      await afterAuth(vaultKey, email, masterPassword, accountKeyPair);
      // New account → activate first-run onboarding (welcome modal + checklist).
      // Gated here (not on "empty safe") so existing users never see it.
      startOnboarding();
      return { ok: true };
    } finally {
      setLoadingStep(null);
    }
  }, [afterAuth]);

  const login = useCallback(async (email: string, masterPassword: string) => {
    await opaque.ready;
    try {
      setLoadingStep('Contacting server…');
      const { clientLoginState, startLoginRequest } = opaque.client.startLogin({ password: masterPassword });

      const startResult = await opaqueLoginStart({ email, startLoginRequest });
      if (!startResult.ok) return { ok: false, error: startResult.error };

      setLoadingStep('Verifying credentials…');
      const loginResult = opaque.client.finishLogin({
        clientLoginState,
        loginResponse: startResult.data.loginResponse,
        password: masterPassword,
      });
      if (!loginResult) return { ok: false, error: 'Invalid credentials' };

      const { finishLoginRequest, exportKey } = loginResult;

      const finishResult = await opaqueLoginFinish({ sessionId: startResult.data.sessionId, finishLoginRequest });
      if (!finishResult.ok) return { ok: false, error: finishResult.error };

      await storeTokens(finishResult.data);

      setLoadingStep('Deriving safe key…');
      const exportWrappingKey = await deriveWrappingKeyFromExportKey(exportKey);
      accountWrappingKey.current = await deriveAccountWrappingKey(exportKey);
      identityWrappingKey.current = await deriveIdentityWrappingKey(exportKey);
      const vaultKey = await unwrapVaultKey(
        finishResult.data.encryptedVaultKey,
        finishResult.data.encryptedVaultKeyIv,
        exportWrappingKey
      );

      await afterAuth(vaultKey, email, masterPassword);
      return { ok: true };
    } finally {
      setLoadingStep(null);
    }
  }, [afterAuth]);

  const unlock = useCallback(async (masterPassword: string) => {
    const email   = getMeta('email');
    const salt    = getMeta('localKdfSalt');
    const wrapped = getMeta('localWrappedSafeKey');
    const wrappedIv = getMeta('localWrappedSafeKeyIv');

    if (!email || !salt || !wrapped || !wrappedIv) {
      return { ok: false, error: 'No safe data found' };
    }

    try {
      const lwk = await deriveLocalWrappingKey(masterPassword, email, salt);
      localWrappingKey.current = lwk;
      const vaultKey = await unwrapVaultKey(wrapped, wrappedIv, lwk);
      encKey.current = vaultKey;
      resetLockTimer();
      // lockVault() cleared loginEmail to null on the auto-lock that brought
      // us here; restore it from the localStorage value used to derive the
      // wrapping key. Without this the SettingsPage email field renders "—"
      // and changeSafePassword fails with "You are not signed in" until the
      // tab is reloaded.
      setLoginEmail(email);
      setStatus('unlocked');

      const [orgsResult, invitesResult] = await Promise.all([listOrgs(), getPendingInvites()]);
      if (invitesResult.ok) setPendingInvites(invitesResult.data.invites);
      if (orgsResult.ok) {
        setOrgs(orgsResult.data.orgs);
        setOrgsLoaded(true);
        orgsRef.current = orgsResult.data.orgs;
        void refreshPendingFinalizations(orgsResult.data.orgs);
      }

      // Phase 3: repopulate AccountKeyContext from the local account-key wrap.
      // This lets us recover the privkey without a full OPAQUE re-login (only
      // the localWrappingKey is available here). The public key is fetched
      // fresh from the server (the page is already online for the orgs list
      // call above; same online assumption).
      if (localWrappingKey.current) {
        const localAccountWrap = getLocalWrappedAccountKey();
        if (localAccountWrap) {
          try {
            const privBytes = await localUnwrapAccountPrivateKey(
              localAccountWrap.ciphertext,
              localAccountWrap.iv,
              localWrappingKey.current,
            );
            const accountKeyResult = await getAccountKey();
            if (accountKeyResult.ok) {
              // PKA finding H1: derive our own account public key from the
              // locally-unwrapped private key rather than trusting the server's
              // copy (see the login path above). The private key here came from
              // the local wrap (PBKDF2 key, never sent to the server), so it is
              // authentic and the derived public key is what we must use.
              const pubBytes = deriveAccountPublicKey(privBytes);
              if (accountKeyResult.data.accountPublicKey !== bytesToBase64Url(pubBytes)) {
                console.warn('Server account public key disagrees with the locally-derived key — using the derived key');
              }
              accountKeys.setKeyPair(pubBytes, privBytes);
            } else {
              // Local unwrap worked but the server wouldn't hand back the public
              // key, so the pair stays absent. Silence here is indistinguishable
              // from "still loading" downstream — an endless "Finishing account
              // setup — try again in a moment." that no retry can clear. Flag it
              // so the UI asks for the one thing that does fix it: a full sign-in.
              console.error('Account key GET failed at unlock', accountKeyResult.error);
              accountKeys.markRecoveryFailed();
            }
          } catch (e) {
            // The local wrap is stale/corrupt (typically after a password change
            // rotated the local wrapping key). Unlock only has the local wrapping
            // key, so it can't fall back to the server wrap — a full sign-in can.
            // Flag it so the UI prompts re-login instead of silently leaving the
            // account key absent (→ "Finishing account setup", undecryptable data).
            console.error('Failed to local-unwrap account key', e);
            accountKeys.markRecoveryFailed();
          }
        }
      }

      // PKA: repopulate the identity keypair from its local wrap (mirrors the
      // account-key repopulation). Only the local wrapping key is available at
      // unlock; the public key is fetched from the server.
      if (localWrappingKey.current) {
        const localIdentityWrap = getLocalWrappedIdentityKey();
        if (localIdentityWrap) {
          try {
            const privBytes = await localUnwrapIdentityPrivateKey(
              localIdentityWrap.ciphertext,
              localIdentityWrap.iv,
              localWrappingKey.current,
            );
            const identityResult = await getIdentityKey();
            if (identityResult.ok) {
              setIdentityKeyPair(base64UrlToBytes(identityResult.data.identityPublicKey), privBytes);
            } else {
              // Same dead end as the account key above. There is no separate
              // identity recoveryFailed flag — the two keys are established
              // together and recovered together, so the account-key flag (and
              // its "sign in again" prompt) covers this case too.
              console.error('Identity key GET failed at unlock', identityResult.error);
              accountKeys.markRecoveryFailed();
            }
          } catch (e) {
            console.error('Failed to local-unwrap identity key', e);
          }
        }
      }

      if (orgsResult.ok) await hydrateFamilyKeys(orgsResult.data.orgs);

      setLoadingStep('Loading safe…');
      removeMeta('lastSyncedAt');

      // Local decrypt succeeded — proves the user knows their safe password.
      // If the refresh token has expired, run a silent OPAQUE re-login using
      // the credentials the user just typed before forcing them through the
      // full login screen. STATUS.md #16.
      const pullResult = await syncPull({ suppressAuthRedirect: true });
      if (pullResult.authExpired) {
        setLoadingStep('Refreshing session…');
        const reloginResult = await login(email, masterPassword);
        if (!reloginResult.ok) {
          // Server rejected the password (rare — local decrypt worked, so
          // typically only happens after server-side password rotation).
          // Fall back to the full login screen.
          encKey.current = null;
          await clearTokens();
          setItems([]);
          setItemsLoaded(false);
          setOrgs([]);
          setOrgsLoaded(false);
          setPendingInvites([]);
          setStatus('unauthenticated');
          return { ok: false, error: 'Your session has expired. Please sign in again.' };
        }
        // login() invoked afterAuth() which already ran syncPull internally.
      }

      void refreshEntitlement();  // best-effort — UI degrades gracefully if it fails
      return { ok: true };
    } catch {
      return { ok: false, error: 'Incorrect safe password' };
    } finally {
      setLoadingStep(null);
    }
  }, [resetLockTimer, syncPull, login, accountKeys, hydrateFamilyKeys, refreshEntitlement, setIdentityKeyPair]);

  const lock = useCallback(() => {
    lockVault();
  }, [lockVault]);

  const logout = useCallback(async () => {
    // Persist any pending debounced edit BEFORE the session is torn down — a
    // check/add/remove made within the debounce window would otherwise be lost
    // (lockVault drops the timer and clears the key, so a late push
    // early-returns). A pending list edit flushes via a full sync (pull-merge-
    // push) so it converges; others just push. Must run before apiLogout revokes
    // the token. Best-effort.
    const listPending = listSyncDebounceRef.current !== null;
    if (pushDebounceRef.current) { clearTimeout(pushDebounceRef.current); pushDebounceRef.current = null; }
    if (listSyncDebounceRef.current) { clearTimeout(listSyncDebounceRef.current); listSyncDebounceRef.current = null; }
    try { if (listPending) await sync(); else await syncPush(); } catch { /* best-effort — don't block logout */ }
    await apiLogout();
    lockVault();
    await clearTokens();
    clearAllLocalData();
    setStatus('unauthenticated');
  }, [lockVault, syncPush, sync]);

  const changeSafePassword = useCallback(async (
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: boolean; error?: string }> => {
    await opaque.ready;

    const email = loginEmail;
    if (!email) return { ok: false, error: 'You are not signed in' };
    // The safeKey must be in memory — the safe has to be unlocked.
    if (!encKey.current) return { ok: false, error: 'Unlock your safe first' };
    // Account key must be hydrated — we need to re-wrap it under the new
    // exportKey-derived wrapping key. If we skip the re-wrap, the on-server
    // blob stays encrypted under the prior exportKey and the next login
    // hits OperationError on unwrap, silently locking the user out of every
    // family-shared item.
    const accountPriv = accountKeys.privateKey;
    if (!accountPriv) {
      return { ok: false, error: 'Account key not loaded — sign in again and retry' };
    }

    try {
      // 1. Start: OPAQUE login-start (current password) + registration-start (new).
      const { clientLoginState, startLoginRequest } = opaque.client.startLogin({ password: currentPassword });
      const { clientRegistrationState, registrationRequest } =
        opaque.client.startRegistration({ password: newPassword });

      const startResult = await passwordRotateStart({ startLoginRequest, registrationRequest });
      if (!startResult.ok) return { ok: false, error: startResult.error };

      // 2. Finish login — a falsy result means the current password was wrong.
      const loginResult = opaque.client.finishLogin({
        clientLoginState,
        loginResponse: startResult.data.loginResponse,
        password: currentPassword,
      });
      if (!loginResult) return { ok: false, error: 'Current password is incorrect' };

      // Finish registration — yields the new OPAQUE record and a new export key.
      const { registrationRecord, exportKey } = opaque.client.finishRegistration({
        clientRegistrationState,
        registrationResponse: startResult.data.registrationResponse,
        password: newPassword,
      });

      // 3a. Re-wrap the in-memory safeKey under the new export key.
      const newWrappingKey = await deriveWrappingKeyFromExportKey(exportKey);
      const { wrapped: newEncryptedVaultKey, iv: newEncryptedVaultKeyIv } =
        await wrapVaultKey(encKey.current, newWrappingKey);

      // 3b. Re-wrap the in-memory account privkey under the new export key —
      // mirrors what afterAuth does on the next login, but server-side rather
      // than locally. The post-rotation login's exportKey is what the unwrap
      // path derives its wrapping key from; the on-server blob has to match.
      const newAccountWrappingKey = await deriveAccountWrappingKey(exportKey);
      const accountWrap = await wrapAccountPrivateKey(accountPriv, newAccountWrappingKey);

      // 3b-ii. PKA: re-wrap the identity privkey under the new export key too, so
      // the next login can unwrap it (the server wrap is keyed off exportKey,
      // which just rotated). Skipped if this session has no identity key yet.
      let identityWrap: { ciphertext: string; iv: string } | undefined;
      if (identityPrivRef.current) {
        const newIdentityWrappingKey = await deriveIdentityWrappingKey(exportKey);
        identityWrap = await wrapIdentityPrivateKey(identityPrivRef.current, newIdentityWrappingKey);
      }

      // 3c. Fresh local-KDF salt for parity with the recovery-reset flow.
      const newLocalKdfSalt = generateLocalKdfSalt();

      const finishResult = await passwordRotateFinish({
        rotationId: startResult.data.rotationId,
        finishLoginRequest: loginResult.finishLoginRequest,
        registrationRecord,
        newEncryptedVaultKey,
        newEncryptedVaultKeyIv,
        newWrappedAccountPrivateKey: accountWrap.ciphertext,
        newWrappedAccountPrivateKeyIv: accountWrap.iv,
        ...(identityWrap
          ? { newWrappedIdentityPrivateKey: identityWrap.ciphertext, newWrappedIdentityPrivateKeyIv: identityWrap.iv }
          : {}),
        newLocalKdfSalt,
      });
      if (!finishResult.ok) return { ok: false, error: finishResult.error };

      // 4. The server swap committed and every session (including this one) was
      //    revoked. Re-login with the new password: this re-acquires Cognito
      //    tokens AND re-wraps the local IndexedDB offline copy via afterAuth.
      const reloginResult = await login(email, newPassword);
      if (!reloginResult.ok) {
        await logout();
        return {
          ok: false,
          error: 'Your safe password was changed. Please sign in again with your new password.',
        };
      }

      // The biometric enrollment wrapped the OLD password — it can't unlock the
      // rotated safe. Clear it; the user re-enables from Settings if they want it.
      clearBiometricEnrollment();
      setBiometricEnrolled(false);
      return { ok: true };
    } catch {
      return { ok: false, error: 'Could not change your safe password' };
    }
  }, [loginEmail, login, logout, accountKeys]);

  // Verifies the safe password without mutating session state. Re-derives the
  // local wrapping key from (password, email, localKdfSalt) and attempts to
  // unwrap the locally cached wrapped vault key. A successful AES-GCM unwrap
  // proves the password is correct — the auth tag catches a wrong key. The
  // result is discarded; encKey / localWrappingKey refs are not touched.
  //
  // Returns false on missing local meta (fresh device) and on any thrown error
  // from the derivation/unwrap path (wrong password is the common case).
  const verifyPassword = useCallback(async (password: string): Promise<boolean> => {
    const email = getMeta('email');
    const salt = getMeta('localKdfSalt');
    const wrapped = getMeta('localWrappedSafeKey');
    const wrappedIv = getMeta('localWrappedSafeKeyIv');
    if (!email || !salt || !wrapped || !wrappedIv) return false;
    try {
      const lwk = await deriveLocalWrappingKey(password, email, salt);
      await unwrapVaultKey(wrapped, wrappedIv, lwk);
      return true;
    } catch {
      return false;
    }
  }, []);

  // ── Biometric unlock (WebAuthn PRF) ────────────────────────────────────────
  // Wraps the safe password behind a platform-authenticator PRF key so the user
  // can unlock with Touch ID / Face ID / Windows Hello instead of retyping it.
  // The recovered password flows into the existing unlock() — no new crypto path.

  const enableBiometricUnlock = useCallback(async (
    safePassword: string,
  ): Promise<{ ok: boolean; code?: string }> => {
    // Never bind a password that doesn't actually unlock this device.
    if (!(await verifyPassword(safePassword))) return { ok: false, code: 'INVALID_PASSWORD' };
    try {
      setBiometricEnrollment(await enrollBiometric(safePassword));
      setBiometricEnrolled(true);
      return { ok: true };
    } catch (e) {
      const code = e instanceof BiometricUnlockError ? e.code : 'FAILED';
      return { ok: false, code };
    }
  }, [verifyPassword]);

  const disableBiometricUnlock = useCallback(() => {
    clearBiometricEnrollment();
    setBiometricEnrolled(false);
  }, []);

  const unlockWithBiometric = useCallback(async (): Promise<{ ok: boolean; error?: string; code?: string }> => {
    const enrollment = getBiometricEnrollment();
    if (!enrollment) return { ok: false, code: 'NOT_ENROLLED' };

    let password: string;
    try {
      password = await unwrapPasswordWithBiometric(enrollment);
    } catch (e) {
      const code = e instanceof BiometricUnlockError ? e.code : 'FAILED';
      // User dismissed the prompt — stay on the lock screen silently.
      if (code === 'USER_CANCELED') return { ok: false, code };
      return { ok: false, error: 'Biometric unlock failed. Enter your safe password.' };
    }

    const result = await unlock(password);
    // A stored password that no longer unlocks (rotated on another device) means
    // the binding is stale — clear it and fall back to the password field.
    if (!result.ok) {
      clearBiometricEnrollment();
      setBiometricEnrolled(false);
      return { ok: false, code: 'STALE', error: 'Biometric unlock is no longer set up. Enter your safe password.' };
    }
    return { ok: true };
  }, [unlock]);

  // PKA: sign a key bundle with the current user's identity private key. Returns
  // null when the identity key isn't loaded (locked / pre-PKA), so callers can
  // decide whether to proceed or defer.
  const signWithIdentityKey = useCallback((fields: SignedKeyFields): SignedKey | null => {
    if (!identityPrivRef.current) return null;
    return signKey(identityPrivRef.current, fields);
  }, []);

  // ── Vault CRUD ────────────────────────────────────────────────────────────────

  const addItem = useCallback(async (item: Omit<VaultItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!encKey.current) throw new Error('Safe is locked');
    const now = new Date().toISOString();
    const newItem: VaultItem = { ...item, id: uuidv4(), createdAt: now, updatedAt: now };
    setItems(prev => [...prev, newItem]);
    dirtyIdsRef.current.add(newItem.id);
    resetLockTimer();
    schedulePush();
    return newItem;
  }, [resetLockTimer, schedulePush]);

  const updateItem = useCallback(async (id: string, updates: Partial<VaultItem>) => {
    if (!encKey.current) throw new Error('Safe is locked');
    const existing = itemsRef.current.find(i => i.id === id);
    if (!existing) throw new Error(`Item ${id} not found`);
    const now = new Date().toISOString();
    const updated: VaultItem = { ...existing, ...updates, id, updatedAt: now };
    // Record a password change into passwordHistory (newest first, capped) so
    // editing a password here is reflected by the detail page's "previous
    // passwords" list, just like the extension. Mirrors the extension's
    // updateItem chokepoint. No-op when the password is unchanged or the caller
    // supplied an explicit history.
    if (
      updates.passwordHistory === undefined &&
      updates.password !== undefined &&
      updates.password !== existing.password
    ) {
      updated.passwordHistory = rotatePasswordHistory(
        existing.passwordHistory,
        existing.password,
        updates.password ?? '',
        now,
      );
    }
    setItems(prev => prev.map(i => i.id === id ? updated : i));
    itemsRef.current = itemsRef.current.map(i => i.id === id ? updated : i);
    dirtyIdsRef.current.add(id);
    resetLockTimer();
    // A list edit is collaborative: reconcile with the server before pushing
    // (pull-merge-push) so it can't clobber another member's concurrent entries.
    // Every other item type keeps the plain debounced push.
    if (updated.type === 'list') scheduleListSync(); else schedulePush();
    return updated;
  }, [resetLockTimer, schedulePush, scheduleListSync]);

  const deleteItem = useCallback(async (id: string) => {
    if (!encKey.current) throw new Error('Safe is locked');
    deletedIdsRef.current.push(id);
    setItems(prev => prev.filter(i => i.id !== id));
    resetLockTimer();
    schedulePush();
  }, [resetLockTimer, schedulePush]);

  // Re-stamp: when a category's circle scope changes, every item filed under it
  // inherits the new scope (denormalized onto the item's plaintext circleId, so
  // the server's existing circle read-filter enforces category visibility with
  // no category→item join). Widening back to whole-family passes circleId
  // undefined, which flags an explicit clear so syncPush sends `null`. Best
  // effort / eventually consistent — other clients converge on their next sync
  // (no proactive narrowing/purge, by design).
  const restampCategory = useCallback((orgId: string, categoryId: string, circleId: string | undefined) => {
    const affected = itemsRef.current.filter((i) => i.orgId === orgId && i.categoryId === categoryId);
    if (affected.length === 0) return;
    const now = new Date().toISOString();
    setItems(prev => prev.map(i =>
      (i.orgId === orgId && i.categoryId === categoryId)
        ? { ...i, circleId: circleId ?? undefined, updatedAt: now }
        : i,
    ));
    for (const i of affected) {
      dirtyIdsRef.current.add(i.id);
      if (circleId) explicitCircleClearRef.current.delete(i.id);
      else explicitCircleClearRef.current.add(i.id);
    }
    resetLockTimer();
    schedulePush();
  }, [resetLockTimer, schedulePush]);

  const getFamilyPrivateKey = useCallback(
    (orgId: string): Uint8Array | undefined => familyKeys.getKey(orgId) ?? undefined,
    [familyKeys],
  );

  return (
    <SafeContext.Provider value={{
      status, items, orgs, orgsLoaded, itemsLoaded, pendingInvites, pendingFinalizations, syncError, loginEmail, loadingStep,
      login, register, unlock, verifyPassword, changeSafePassword, lock, logout,
      biometricEnrolled, enableBiometricUnlock, disableBiometricUnlock, unlockWithBiometric,
      addItem, updateItem, deleteItem, restampCategory, getFamilyPrivateKey, sync, resyncSafe, refreshOrgs, refreshInvites, refreshPendingFinalizations,
      accountWrappingKey: accountWrappingKey.current,
      localWrappingKey: localWrappingKey.current,
      vaultKey: encKey.current,
      identityPublicKey: identityPubRef.current,
      signWithIdentityKey,
      entitlement,
      transientBannerMessage,
      dismissTransientBanner,
      refreshEntitlement,
      emailVerified,
      activePromoCode,
    }}>
      {children}
    </SafeContext.Provider>
  );
}
